/*
 * lab-vt.js — name the view transition at the click, not in the markup.
 *
 * A cross-document view transition pairs an element on this page with one of the same
 * `view-transition-name` on the next. The index pages used to carry that name inline on
 * every row, which is correct but expensive: a named element is its own stacking context,
 * and a dozen of them on a scrolling list cost more than the transition they enable.
 * Measured on /blogs/news at 390 with the CPU throttled 4x: 2720 ms of main-thread task
 * for one full-page scroll, 1280 ms with the names removed.
 *
 * Only one pair can ever transition, and we know which one at the moment of the click.
 * The rows now carry `data-vt` (inert) and this applies the real name to the row being
 * followed, on pointerdown so the name is set before the browser takes its snapshot.
 * The destination page keeps its name in the markup — one element, no cost.
 *
 * Without JS, or where view transitions are unsupported, nothing is named and navigation
 * is a plain load: the transition is an enhancement, never a dependency.
 */
(() => {
  if (!document.startViewTransition) return;

  const apply = (el) => {
    const name = el.getAttribute('data-vt');
    if (!name || el.style.viewTransitionName) return;
    el.style.viewTransitionName = name;
  };

  // A product row carries its own name and holds a media link carrying another; both
  // belong to the pair being followed. Climbing to the nearest [data-vt] would stop at
  // the media link when that is what was clicked, so climb to the row instead and name
  // everything inside it — never anything outside.
  const arm = (link) => {
    const row = link.closest('li, article, [data-row]') || link;
    if (row.hasAttribute('data-vt')) apply(row);
    row.querySelectorAll('[data-vt]').forEach(apply);
    if (link.hasAttribute('data-vt')) apply(link);
  };

  const onDown = (event) => {
    const link = event.target.closest('a[href]');
    if (!link) return;
    if (link.target === '_blank' || link.hasAttribute('download')) return;
    if (link.origin && link.origin !== window.location.origin) return;
    arm(link);
  };

  document.addEventListener('pointerdown', onDown, { capture: true, passive: true });
  // keyboard and assistive activation never fire pointerdown
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const link = event.target.closest && event.target.closest('a[href]');
    if (link) arm(link);
  }, { capture: true });

  // A back/forward restore reuses this document: clear the names so the list is cheap
  // again and the next click names its own pair.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll('[data-vt]').forEach((el) => { el.style.viewTransitionName = ''; });
  });
})();
